/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { getAiPlannerEmbedReceiverId } from '@atlassian/ai-planner-embed-bridge/AiPlannerBridgeCommands';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import { getDocument } from '@jira/ssr__browser-globals/src/getDocument.tsx';
import { getLocation } from '@jira/ssr__browser-globals/src/getLocation.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';
import { RenderOnClient } from '@jira/ssr__ssr-toolkit/src/RenderOnClient.tsx';
import { aiPlannerReceiverIdAttribute } from './aiPlannerReceiverIdAttribute.tsx';
import { getAiPlannerOrigin } from './getAiPlannerOrigin.tsx';
import { type AiPlannerEmbedContext, getAiPlannerUrl } from './getAiPlannerUrl.tsx';
import { getBifrostOverrideFromLocation } from './getBifrostOverrideFromLocation.tsx';
import { getValidatedPlannerNavigationPath } from './getValidatedPlannerNavigationPath.tsx';
import { isMoveBeforeSupported } from './isMoveBeforeSupported.tsx';
import { moveNodeBefore } from './moveNodeBefore.tsx';
import { useAiPlannerBridge } from './useAiPlannerBridge.tsx';
import { useAiPlannerColorMode } from './useAiPlannerColorMode.tsx';

// If the iframe has not signalled that its document is ready within this
// window, we treat the load as failed and surface the error fallback.
const LOAD_TIMEOUT_MS = 15_000;

const noop = (): void => undefined;

type AiPlannerIframeStatus = 'idle' | 'loading' | 'loaded' | 'error';

type AiPlannerIframeClaim = {
	/** Element the shared iframe should be moved into while claimed. */
	target: HTMLElement;
	/**
	 * Called when the planner asks the host to perform shell-level back
	 * navigation (the user pressed the planner's in-app back button).
	 */
	onBack?: () => void;
	/**
	 * Called when the planner asks the host to dismiss/close the embed (the user
	 * pressed the planner's in-app close button) — e.g. close the modal.
	 */
	onClose?: () => void;
	/**
	 * Called when the visible iframe request reaches a ready state.
	 * `fromWarmShell` is true when the planner was already showing the requested
	 * content, so the user waited for nothing — the signal that pre-warming paid
	 * off for this open.
	 */
	onLoad?: (info: { fromWarmShell: boolean }) => void;
	/** Called when the visible iframe request errors or times out. */
	onError?: (reason: 'error' | 'timeout') => void;
	/** Called when the claim is released before reaching a terminal state. */
	onAbort?: () => void;
};

type AiPlannerIframeClaimResult = {
	/**
	 * False when `moveBefore` is unsupported, the host is not ready, or the
	 * move threw. The caller must switch to a standalone iframe — never leave
	 * the user on the shared loading overlay with a parked iframe.
	 */
	ok: boolean;
	/** Parks the host. Idempotent and generation-scoped. */
	release: () => void;
};

const FAILED_CLAIM: AiPlannerIframeClaimResult = { ok: false, release: noop };

type AiPlannerIframeContextValue = {
	/** Confluence space supplied by the host for Planner publication defaults. */
	defaultConfluenceSpaceKey?: string;
	/** Current status of the shared planner iframe for the active request. */
	status: AiPlannerIframeStatus;
	/**
	 * True once the host node has mounted on the client. Claimers must wait for
	 * it: the host's ref attaches after their own layout effects run, so a claim
	 * on first mount would find nothing to move.
	 */
	isHostReady: boolean;
	/**
	 * Boot the persistent iframe (at the stable planner root URL) in the
	 * background without revealing it. Safe to call repeatedly — the SPA boots
	 * once and is then driven via the bridge. `planId` is sent ahead via the
	 * bridge so the planner can pre-navigate while still hidden.
	 */
	warm: (planId?: string) => void;
	/**
	 * Ask the planner to warm the full-plan cache for the given plan ids ahead of
	 * a likely navigation (e.g. recent plans, or a hovered plan row), so a later
	 * `claim`/`warm` for one of them renders instantly. Boots the iframe if it is
	 * not already mounted. Best-effort, deduped and non-blocking — safe to call
	 * generously (e.g. on every hover).
	 */
	prewarm: (planIds: readonly string[]) => void;
	/**
	 * Take ownership of the shared iframe: `moveBefore` the host into `target`
	 * immediately (never `appendChild`).
	 * Ask the planner (over the bridge) to navigate to `planId` (or root) — the
	 * iframe is NEVER reloaded. `release` returns the iframe to its hidden host.
	 */
	claim: (claim: AiPlannerIframeClaim, planId?: string) => AiPlannerIframeClaimResult;
	/**
	 * Parks the currently claimed host immediately — the active generation's
	 * `release`. Triggers call it from `entryPointProps.onClose`, which runs
	 * before `closeModal`/`dispose`, so the host is moved home while the slot is
	 * still connected.
	 */
	releaseActiveClaim: () => void;
	/** Force a fresh reload after a failure. */
	retry: () => void;
};

const AiPlannerIframeContext = createContext<AiPlannerIframeContextValue | null>(null);

type AiPlannerIframeProviderProps = {
	/** Tenant context used to build the planner embed URL. */
	embedContext: AiPlannerEmbedContext;
	/** Jira space to preselect when Planner opens its create experience. */
	defaultJiraSpaceKey?: string;
	/** Confluence space to preselect when Planner opens its publication experience. */
	defaultConfluenceSpaceKey?: string;
	children: ReactNode;
};

/**
 * Owns a single, persistent Planner `<iframe>` so it can be warmed before the
 * modal opens and then reused — the modal reveals this one iframe by moving its
 * DOM node into the modal body instead of mounting its own, so a warmed planner
 * opens instantly with no remount or re-download.
 *
 * Mirrors the planner's own `ConfluenceBridgeProvider` pattern: lazy first
 * mount on intent, kept hidden until claimed, and reused across claims.
 *
 * React still portals a **stable parking wrapper** that is never moved. Only
 * the inner host node is relocated with `moveBefore`.
 */
export const AiPlannerIframeProvider = ({
	embedContext,
	defaultJiraSpaceKey,
	defaultConfluenceSpaceKey,
	children,
}: AiPlannerIframeProviderProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { push } = useRouterActions();
	const parkingRef = useRef<HTMLDivElement | null>(null);
	const hostRef = useRef<HTMLDivElement | null>(null);
	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	// Stable per-mount id giving this iframe a private Rovo bridge channel, so it
	// never collides with another planner iframe on the shared bridge.
	// oxlint-disable-next-line react/refs
	const bridgeInstanceId = useRef<string>(getWindow()?.crypto.randomUUID() ?? 'ssr').current;

	const [status, setStatus] = useState<AiPlannerIframeStatus>('idle');
	// Set once the iframe should exist in the DOM (after first warm/claim).
	const [isMounted, setIsMounted] = useState(false);
	// True while the iframe is revealed in a modal. Background warming is silent
	// (no overlay, no timeout); only a claimed/visible iframe drives status and
	// the load timeout so a slow background boot never poisons the warm.
	const [isClaimed, setIsClaimed] = useState(false);
	// True once the parking wrapper (and inner host) have mounted on the client.
	const [isHostReady, setIsHostReady] = useState(false);
	// Bumping this forces a remount of the iframe for a clean retry.
	const [loadKey, setLoadKey] = useState(0);
	const settledRef = useRef(false);
	// Bumped on each successful claim so a stale release cannot steal the host
	// from a newer claim (rapid close/reopen).
	const claimGenerationRef = useRef(0);
	const releaseActiveClaimRef = useRef<() => void>(noop);

	// Jira's live theme, never the OS preference (see useAiPlannerColorMode).
	const colorMode = useAiPlannerColorMode();

	// Root URL — stable across plan navigation (never changes `src` for a plan
	// switch; that happens over the bridge via `sendLoadPlan`) but recomputed
	// when `colorMode` changes, since Planner only reads colorMode once per
	// iframe load. The effect below detects that recompute and forces the
	// already-booted iframe to reload so the new value actually takes effect.
	const src = useMemo(
		// A planner Bifrost build can be pinned via the page URL's
		// `ai-planner-bifrost-override` query param (see getBifrostOverrideFromLocation).
		() =>
			getAiPlannerUrl(
				embedContext,
				undefined,
				getBifrostOverrideFromLocation() ?? undefined,
				undefined,
				bridgeInstanceId,
				colorMode,
			),
		[embedContext, bridgeInstanceId, colorMode],
	);

	// Tracks the requestId of the most recent loadPlan so we only react to the
	// planner's matching `ready` transition (ignoring stale/echoed ones).
	const pendingRequestIdRef = useRef<string | null>(null);

	// The most recent plan id requested (via warm/claim); undefined means the
	// planner root. Replayed after a colorMode-triggered reload so the fresh
	// iframe resumes wherever the user (or a warm) had navigated to.
	const activePlanIdRef = useRef<string | undefined>(undefined);

	// The plan id the planner last confirmed via a matching `ready` transition,
	// or null once it has been asked for something else or lost its document.
	// A loadPlan for the plan already on screen is a planner-side no-op, so
	// without this a repeat request would wait for a `ready` that never comes.
	const loadedPlanIdRef = useRef<string | null>(null);

	// Set right before a colorMode-triggered reload and consumed by handleLoad,
	// so the freshly-reloaded iframe is re-pointed at the plan it lost when its
	// document was discarded (plan navigation normally never reloads the
	// iframe — see the `src` comment above — so this only fires for a theme
	// change, never for an ordinary plan switch).
	const reloadPendingRef = useRef(false);

	// True once the iframe's root document (the SPA shell) has loaded at least
	// once. After that, the same persistent iframe is reused and won't fire
	// `onLoad` again, so claims rely on this + bridge transitions.
	const shellLoadedRef = useRef(false);

	// Back/close intents are owned by whoever currently has the iframe claimed
	// (the open modal). We hold the active claim's handlers in a ref so the bridge
	// client is created once and always calls the latest handlers.
	const claimIntentsRef = useRef<
		Pick<AiPlannerIframeClaim, 'onAbort' | 'onBack' | 'onClose' | 'onError' | 'onLoad'>
	>({});

	const markVisibleLoadSucceeded = useCallback((fromWarmShell = false): boolean => {
		if (settledRef.current) {
			return false;
		}
		settledRef.current = true;
		setStatus((currentStatus) => (currentStatus === 'idle' ? currentStatus : 'loaded'));
		claimIntentsRef.current.onLoad?.({ fromWarmShell });
		return true;
	}, []);

	const markVisibleLoadFailed = useCallback((reason: 'error' | 'timeout'): boolean => {
		if (settledRef.current) {
			return false;
		}
		settledRef.current = true;
		// A failed load must never be short-circuited as "already showing".
		loadedPlanIdRef.current = null;
		setStatus((currentStatus) => (currentStatus === 'idle' ? currentStatus : 'error'));
		claimIntentsRef.current.onError?.(reason);
		return true;
	}, []);

	const handleNavigate = useCallback(
		(url: string) => {
			const location = getLocation();
			if (location == null) {
				return;
			}
			const path = getValidatedPlannerNavigationPath(url, location.origin);
			if (path != null) {
				push(path);
			}
		},
		[push],
	);

	// oxlint-disable-next-line react/refs
	const { sendLoadPlan, sendPrewarmPlans, sendVisibility } = useAiPlannerBridge({
		bridgeInstanceId,
		defaultJiraSpaceKey,
		defaultConfluenceSpaceKey,
		onTransition: useCallback(
			(transition) => {
				if (transition.requestId !== pendingRequestIdRef.current) {
					return;
				}
				if (transition.state === 'ready') {
					loadedPlanIdRef.current = activePlanIdRef.current ?? null;
					markVisibleLoadSucceeded();
				}
			},
			[markVisibleLoadSucceeded],
		),
		onBack: useCallback(() => {
			claimIntentsRef.current.onBack?.();
		}, []),
		onClose: useCallback(() => {
			claimIntentsRef.current.onClose?.();
		}, []),
		onNavigate: handleNavigate,
	});

	// Preconnect to the planner origin (only when cross-origin) so the TLS/DNS
	// connection is warm before the iframe loads. No-op when same-origin.
	useEffect(() => {
		const origin = getAiPlannerOrigin();
		const doc = getDocument();
		if (origin == null || doc == null) {
			return undefined;
		}
		const link = doc.createElement('link');
		link.rel = 'preconnect';
		link.href = origin;
		link.crossOrigin = '';
		doc.head.appendChild(link);
		return () => {
			doc.head.removeChild(link);
		};
	}, []);

	// Mount the persistent iframe once (boots the planner SPA at the root URL).
	// Stable identity: reads live state via refs, so it never re-creates and
	// never triggers claim/release churn.
	const bootRef = useRef(false);
	// Boot the persistent iframe once. This is SILENT — it never sets a visible
	// status or arms the timeout, so a slow background boot cannot error the
	// warm. The claimed/visible path owns status + timeout.
	const boot = useCallback(() => {
		if (bootRef.current) {
			return;
		}
		if (!isMoveBeforeSupported()) {
			return;
		}
		bootRef.current = true;
		setIsMounted(true);
	}, []);

	// Ask the planner to navigate (no reload). Records the requestId so we can
	// match the planner's `ready` transition. `visible` is true for a claim
	// (drives the overlay/timeout); false for background warming (stays silent).
	// The same boolean is sent over the bridge so the planner can pause
	// placeholder animations while hidden.
	const requestPlan = useCallback(
		(nextPlanId: string | undefined, visible: boolean) => {
			sendVisibility(visible);
			// Re-requesting the confirmed plan leaves the planner silent; anything
			// else makes it navigate and re-confirm with a fresh `ready`.
			const isAlreadyShowing =
				shellLoadedRef.current && nextPlanId != null && nextPlanId === loadedPlanIdRef.current;
			if (!isAlreadyShowing) {
				loadedPlanIdRef.current = null;
			}
			activePlanIdRef.current = nextPlanId;
			const requestId = sendLoadPlan(nextPlanId);
			pendingRequestIdRef.current = requestId;
			if (!visible) {
				return;
			}
			settledRef.current = false;
			// For a visible claim, show loading until ready — unless there is
			// nothing for the planner to navigate to, in which case it is already
			// showing the right content: the root of an already-booted shell, or a
			// plan it has confirmed and not left since.
			const isRoot = nextPlanId == null;
			if (shellLoadedRef.current && (isRoot || isAlreadyShowing)) {
				// Set the status explicitly: a silent warm may have settled the shell
				// while the status was still 'idle', and `markVisibleLoadSucceeded`
				// leaves 'idle' alone so background boots stay silent. Claimers render
				// 'idle' as loading and the timeout only arms for 'loading', so without
				// this the claim shows a spinner nothing can clear.
				setStatus('loaded');
				markVisibleLoadSucceeded(true);
				return;
			}
			setStatus('loading');
		},
		[markVisibleLoadSucceeded, sendLoadPlan, sendVisibility],
	);

	const warm = useCallback(
		(nextPlanId?: string) => {
			if (!isMoveBeforeSupported()) {
				return;
			}
			boot();
			requestPlan(nextPlanId, false);
		},
		[boot, requestPlan],
	);

	const prewarm = useCallback(
		(planIds: readonly string[]) => {
			if (!isMoveBeforeSupported()) {
				return;
			}
			// Boot the (hidden) iframe so the planner is alive to receive and act on
			// the prewarm; the warming itself is silent and never blocks.
			boot();
			sendPrewarmPlans(planIds);
		},
		[boot, sendPrewarmPlans],
	);

	const parkHost = useCallback(() => {
		moveNodeBefore(hostRef.current, parkingRef.current);
	}, []);

	const bindRelease = useCallback((release: () => void) => {
		releaseActiveClaimRef.current = release;
		return release;
	}, []);

	const claim = useCallback(
		(
			{ target, onAbort, onBack, onClose, onError, onLoad }: AiPlannerIframeClaim,
			nextPlanId?: string,
		): AiPlannerIframeClaimResult => {
			if (!isMoveBeforeSupported()) {
				return FAILED_CLAIM;
			}
			const host = hostRef.current;
			if (!moveNodeBefore(host, target)) {
				return FAILED_CLAIM;
			}
			boot();
			const generation = claimGenerationRef.current + 1;
			claimGenerationRef.current = generation;
			setIsClaimed(true);
			// Route the planner's back/close intents to this claimer (the open
			// modal) for as long as it holds the iframe.
			claimIntentsRef.current = { onAbort, onBack, onClose, onError, onLoad };
			requestPlan(nextPlanId, true);
			const release = bindRelease(() => {
				if (generation !== claimGenerationRef.current) {
					return;
				}
				if (!settledRef.current) {
					claimIntentsRef.current.onAbort?.();
				}
				parkHost();
				sendVisibility(false);
				setIsClaimed(false);
				claimIntentsRef.current = {};
				releaseActiveClaimRef.current = noop;
			});
			return { ok: true, release };
		},
		[bindRelease, boot, parkHost, requestPlan, sendVisibility],
	);

	const releaseActiveClaim = useCallback(() => {
		releaseActiveClaimRef.current();
	}, []);

	// Why the document currently loading exists, reported on the load event so a
	// theme reload or a retry is not counted as a cold boot.
	const loadTriggerRef = useRef<'boot' | 'colorMode' | 'retry'>('boot');

	const retry = useCallback(() => {
		loadTriggerRef.current = 'retry';
		settledRef.current = false;
		// Recreating the iframe discards the document, and with it whatever the
		// planner had confirmed.
		loadedPlanIdRef.current = null;
		setStatus('loading');
		setLoadKey((key) => key + 1);
	}, []);

	// `src` only changes when Jira's theme does, and Planner reads colorMode once
	// per document (see getAiPlannerUrl), so the new value can only be applied by
	// reloading: bump `loadKey` to recreate the iframe, and `handleLoad` replays
	// the active plan into the fresh document. Adjusted during render rather than
	// in an effect because the reload belongs to the render that delivers the new
	// `src`.
	const prevSrcRef = useRef(src);
	// oxlint-disable-next-line react/refs
	if (prevSrcRef.current !== src) {
		// oxlint-disable-next-line react/refs
		prevSrcRef.current = src;
		// oxlint-disable-next-line react/refs
		if (bootRef.current) {
			// oxlint-disable-next-line react/refs
			shellLoadedRef.current = false;
			// oxlint-disable-next-line react/refs
			reloadPendingRef.current = true;
			setLoadKey((key) => key + 1);
			if (isClaimed) {
				// oxlint-disable-next-line react/refs
				settledRef.current = false;
				setStatus('loading');
			}
		}
	}

	const handleLoad = useCallback(() => {
		// The SPA shell has loaded — record it so later claims know the iframe is
		// already booted (it won't fire onLoad again once reused).
		shellLoadedRef.current = true;
		fireOperationalAnalytics(createAnalyticsEvent({}), 'aiPlannerIframe loaded', {
			path: 'shared',
			// A background warm loads with nobody watching. Without this, silent
			// boots and real opens land in the same bucket and no reliability rate
			// computed from these events means anything.
			visible: isClaimed,
			trigger: reloadPendingRef.current ? 'colorMode' : loadTriggerRef.current,
		});
		loadTriggerRef.current = 'boot';
		if (reloadPendingRef.current) {
			// A theme reload, not the initial boot: the fresh document discarded
			// whatever the planner was showing, so forget the confirmed plan and
			// re-request it. Nothing can act on the stale id before this point —
			// every short-circuit that reads it also requires `shellLoadedRef`,
			// which stays false until the reload lands.
			reloadPendingRef.current = false;
			loadedPlanIdRef.current = null;
			requestPlan(activePlanIdRef.current, isClaimed);
			return;
		}
		// Only lift the overlay when the iframe is actually visible in a modal;
		// background warming stays silent. (A specific-plan claim still waits for
		// the bridge `ready` transition — but the shell load is a safe fallback.)
		markVisibleLoadSucceeded();
	}, [createAnalyticsEvent, isClaimed, markVisibleLoadSucceeded, requestPlan]);

	const handleError = useCallback(() => {
		// Only surface the error when visible; a failed background warm is silent
		// and will be retried/handled when the user actually opens the modal.
		if (!markVisibleLoadFailed('error')) {
			return;
		}
		fireOperationalAnalytics(createAnalyticsEvent({}), 'aiPlannerIframe failed', {
			reason: 'error',
			path: 'shared',
			visible: isClaimed,
		});
	}, [createAnalyticsEvent, isClaimed, markVisibleLoadFailed]);

	// Treat a stalled load as a failure — but ONLY while the iframe is visible in
	// a modal. Background warming never times out, so a slow cold boot can't
	// poison the warm before the user even opens it.
	useEffect(() => {
		if (!isClaimed || status !== 'loading') {
			return undefined;
		}
		const timeoutId = setTimeout(() => {
			if (!markVisibleLoadFailed('timeout')) {
				return;
			}
			fireOperationalAnalytics(createAnalyticsEvent({}), 'aiPlannerIframe failed', {
				reason: 'timeout',
				path: 'shared',
				// The timeout only arms for a claimed iframe, so this is always a
				// user-visible failure.
				visible: true,
			});
		}, LOAD_TIMEOUT_MS);
		return () => {
			clearTimeout(timeoutId);
		};
		// oxlint-disable-next-line react/exhaustive-effect-dependencies
	}, [isClaimed, status, loadKey, createAnalyticsEvent, markVisibleLoadFailed]);

	// Once the host is in the slot and the document is ready, send keyboard
	// focus into the iframe so Tab is not required after a click.
	const didFocusRef = useRef(false);
	useEffect(() => {
		if (!isClaimed || status !== 'loaded') {
			if (!isClaimed) {
				didFocusRef.current = false;
			}
			return undefined;
		}
		if (didFocusRef.current) {
			return undefined;
		}
		didFocusRef.current = true;
		try {
			iframeRef.current?.focus();
		} catch {
			// jsdom implements HTMLElement.focus but not window.focus.
		}
		return undefined;
	}, [isClaimed, status]);

	const value = useMemo<AiPlannerIframeContextValue>(
		() => ({
			defaultConfluenceSpaceKey,
			status,
			isHostReady,
			warm,
			prewarm,
			claim,
			releaseActiveClaim,
			retry,
		}),
		[
			defaultConfluenceSpaceKey,
			status,
			isHostReady,
			warm,
			prewarm,
			claim,
			releaseActiveClaim,
			retry,
		],
	);

	const hostRefCallback = useCallback((node: HTMLDivElement | null) => {
		hostRef.current = node;
		setIsHostReady(node != null);
	}, []);

	/*
	 * Parking wrapper is the stable React portal target and is never moved.
	 * The inner host is the node `moveBefore` relocates into the modal slot.
	 */
	const host = (
		<div ref={parkingRef}>
			<div
				ref={hostRefCallback}
				css={[
					isClaimed ? styles.hostInSlot : styles.host,
					isClaimed ? styles.hostVisible : styles.hostHidden,
				]}
				aria-hidden={isClaimed ? undefined : 'true'}
			>
				{isMounted && (
					// eslint-disable-next-line jira/restricted/iframe
					<iframe
						key={loadKey}
						ref={iframeRef}
						css={styles.iframe}
						src={src}
						title="Planner"
						// Required by the Rovo content bridge so loadPlan commands are
						// routed to the planner window. Must match the planner's child id.
						{...{ [aiPlannerReceiverIdAttribute]: getAiPlannerEmbedReceiverId(bridgeInstanceId) }}
						onLoad={handleLoad}
						onError={handleError}
					/>
				)}
			</div>
		</div>
	);

	const doc = getDocument();

	return (
		<AiPlannerIframeContext.Provider value={value}>
			{children}
			<RenderOnClient>{doc?.body != null ? createPortal(host, doc.body) : null}</RenderOnClient>
		</AiPlannerIframeContext.Provider>
	);
};

export const useAiPlannerIframe = (): AiPlannerIframeContextValue | null =>
	useContext(AiPlannerIframeContext);

const styles = cssMap({
	host: {
		position: 'fixed',
		overflow: 'hidden',
	},
	// Claimed: fill the modal slot. Not position:fixed — the host is a
	// descendant of the dialog (including native top-layer).
	hostInSlot: {
		width: '100%',
		height: '100%',
		overflow: 'hidden',
		display: 'flex',
	},
	// Parked: collapsed, out of layout and the a11y tree, not interactive.
	hostHidden: {
		// display:none prevents unclaimed iframe host from taking focus
		display: 'none',
	},
	// Claimed: visible, interactive and filling the slot it was moved into.
	hostVisible: {
		opacity: 1,
		pointerEvents: 'auto',
	},
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
	},
});
